import React from "react";
import { CircularProgress } from "@mui/material";
import {
  FeedCard,
  FeedHeader,
  FeedTitle,
  FeedItem,
  FeedContent,
  FeedText,
  FeedTime,
  FeedActions,
  ActionButton,
  LoadingContainer,
  ErrorContainer,
  ErrorText,
  RetryButton,
  EmptyStateContainer,
  EmptyStateText,
  FeedItemContent,
  FeedItemIcon,
  FeedItemText,
  FeedListContainer,
} from "./FeedPanel.styled";
import {
  CheckIcon,
  PersonIcon,
  DoubleCheckIcon,
  PersonCheckIcon,
  PersonDeleteIcon,
  AddAppointmentIcon,
} from "../Icons";
import IconContainer from "../Icons/IconContainer";
import { colors } from "../../theme/colors";

export interface FeedItemData {
  id: number;
  text: string;
  time: string;
  type?: number;
  isRead?: boolean;
  icon?: React.ReactNode;
  patientName?: string;
}

interface FeedPanelProps {
  title: string;
  items: FeedItemData[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onItemAction?: (itemId: number) => void;
  actionButtonText?: string;
  emptyMessage?: string;
  className?: string;
}

const FeedPanel: React.FC<FeedPanelProps> = ({
  title,
  items,
  loading,
  error,
  onRetry,
  onItemAction,
  actionButtonText,
  emptyMessage = "No items at this time",
  className,
}) => {
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  };

  const formatTextWithBoldName = (text: string, patientName?: string) => {
    if (!patientName) return text;

    const parts = text.split(patientName);
    if (parts.length === 1) return text;

    return (
      <>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {part}
            {index < parts.length - 1 && <strong>{patientName}</strong>}
          </React.Fragment>
        ))}
      </>
    );
  };

  const getDefaultIcon = (type?: number) => {
    switch (type) {
      case 44: // TreatmentPlanApprovedPatient - Patient accepted
        return (
          <IconContainer
            size={24}
            borderRadius="4px"
            bgcolor={colors.background.lightBlue}
          >
            <PersonCheckIcon />
          </IconContainer>
        );
      case 45: // TreatmentPlanRejectedPatient - Patient declined
        return (
          <IconContainer
            size={24}
            borderRadius="4px"
            bgcolor={colors.background.red}
          >
            <PersonDeleteIcon />
          </IconContainer>
        );
      case 40: // TreatmentPlanGenerated - AI plan awaiting review
        return (
          <IconContainer
            size={24}
            borderRadius="4px"
            bgcolor={colors.background.lightBlue}
          >
            <AddAppointmentIcon />
          </IconContainer>
        );
      default:
        return <PersonIcon />;
    }
  };

  if (loading) {
    return (
      <FeedCard className={className}>
        <FeedHeader>
          <FeedTitle>{title}</FeedTitle>
        </FeedHeader>
        <LoadingContainer>
          <CircularProgress size={24} />
        </LoadingContainer>
      </FeedCard>
    );
  }

  if (error) {
    return (
      <FeedCard className={className}>
        <FeedHeader>
          <FeedTitle>{title}</FeedTitle>
        </FeedHeader>
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
          <RetryButton onClick={onRetry}>Retry</RetryButton>
        </ErrorContainer>
      </FeedCard>
    );
  }

  return (
    <FeedCard className={className}>
      <FeedHeader>
        <FeedTitle>{title}</FeedTitle>
      </FeedHeader>

      {items.length === 0 ? (
        <EmptyStateContainer>
          <EmptyStateText>{emptyMessage}</EmptyStateText>
        </EmptyStateContainer>
      ) : (
        <FeedListContainer>
          {items.map((item) => (
            <FeedItem key={item.id} data-isread={item.isRead}>
              <FeedContent>
                <FeedItemContent>
                  <FeedItemIcon>
                    {item.icon || getDefaultIcon(item.type)}
                  </FeedItemIcon>
                  <FeedItemText>
                    <FeedText>
                      {formatTextWithBoldName(item.text, item.patientName)}
                    </FeedText>
                    <FeedTime>{formatTimeAgo(item.time)}</FeedTime>
                  </FeedItemText>
                </FeedItemContent>
                {onItemAction && actionButtonText && (
                  <FeedActions>
                    {!item.isRead ? (
                      <ActionButton
                        size="small"
                        onClick={() => onItemAction(item.id)}
                        title={actionButtonText}
                      >
                        <CheckIcon />
                      </ActionButton>
                    ) : (
                      <DoubleCheckIcon />
                    )}
                  </FeedActions>
                )}
              </FeedContent>
            </FeedItem>
          ))}
        </FeedListContainer>
      )}
    </FeedCard>
  );
};

export default FeedPanel;
